import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useLang } from '@/lib/i18n';
import { Logo } from '@/components/Logo';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWith } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/login', { email, password });
      loginWith(r.data.token, r.data.user);
      toast.success(`Bienvenue ${r.data.user.first_name} !`);
      if (r.data.user.role === 'admin') nav('/admin');
      else nav('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connexion échouée');
    } finally { setLoading(false); }
  };

  const fillDemo = (which) => {
    if (which === 'jobeur') { setEmail('jobeur@demo.cd'); setPassword('demo123'); }
    if (which === 'client') { setEmail('client@demo.cd'); setPassword('demo123'); }
    if (which === 'admin') { setEmail('admin@jobcongo.cd'); setPassword('admin123'); }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFF] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <Logo size="xl" variant="default" />
          <p className="mt-3 text-2xl font-bold tracking-tight text-[#0E3F80]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Job<span className="text-[#1677F2]">Kongo</span>
          </p>
          <p className="text-xs text-[#16764A] font-medium mt-0.5">Mosala na confiance</p>
        </div>
        <Card className="p-6 md:p-8">
          <h1 className="text-2xl font-bold text-[#0B1220]">{t('auth.login.title')}</h1>
          <p className="text-sm text-slate-600 mt-1">{t('auth.login.subtitle')}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label>{t('auth.email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>{t('auth.password')}</Label>
                <Link to="/forgot-password" className="text-xs text-[#1677F2] font-medium hover:underline" data-testid="login-forgot-link">
                  {t('auth.forgot')}
                </Link>
              </div>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" disabled={loading} data-testid="login-submit-button">
              {loading ? t('auth.login.loading') : t('auth.login.button')}
            </Button>
          </form>
          <div className="mt-6 border-t border-[#E2E8F0] pt-4">
            <p className="text-xs text-slate-600 mb-2 font-medium">{t('auth.demo')}</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => fillDemo('jobeur')} data-testid="login-demo-jobeur">Jobeur demo</Button>
              <Button variant="outline" size="sm" onClick={() => fillDemo('client')} data-testid="login-demo-client">Client demo</Button>
              <Button variant="outline" size="sm" onClick={() => fillDemo('admin')} data-testid="login-demo-admin">Admin</Button>
            </div>
          </div>
          <p className="text-center text-sm text-slate-600 mt-6">
            {t('auth.noAccount')} <Link to="/signup" className="text-[#1677F2] font-medium" data-testid="login-signup-link">{t('auth.createAccount')}</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
